<template>
  <div :class="embedded ? 'institution-tfa-embedded' : 'card card-shape home-box border-start border-4 border-success shadow-sm mt-4'">
    <div v-if="!embedded" class="card-header bg-light">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h6 class="m-0 fw-bold text-green">
            <font-awesome-icon :icon="['fas', 'key']" class="me-2 text-success" />
            Security &amp; two-factor authentication
          </h6>
          <div class="small text-muted mt-1">
            Help protect everyone who signs in by choosing how second-step verification works for your organisation.
          </div>
        </div>
      </div>
    </div>
    <div v-else class="mb-3 pb-2 border-bottom border-success border-opacity-25">
      <h6 class="m-0 fw-bold text-green">
        <font-awesome-icon :icon="['fas', 'key']" class="me-2 text-success" />
        Security &amp; two-factor authentication
      </h6>
      <div class="small text-muted mt-1">
        Help protect everyone who signs in by choosing how second-step verification works for your organisation.
      </div>
    </div>
    <div :class="embedded ? '' : 'card-body'">
      <b-alert v-if="!canView" show variant="secondary" class="mb-0">
        You do not have permission to view these settings.
      </b-alert>

      <div v-else>
        <b-form @submit.prevent="save">
          <b-form-group
            label="Require 2FA at login"
            label-for="tfa-enabled"
            description="When enabled, users must complete a second step after username and password."
          >
            <b-form-checkbox
              id="tfa-enabled"
              v-model="local.two_factor_authentication_enabled"
              :disabled="!canEdit"
              switch
              size="lg"
            />
          </b-form-group>

          <template v-if="local.two_factor_authentication_enabled">
            <b-form-group label="Delivery method" class="mt-3">
              <b-form-radio-group
                v-model="local.two_factor_delivery_method"
                :options="deliveryOptions"
                :disabled="!canEdit"
                name="tfa-delivery"
                stacked
              />
            </b-form-group>

            <b-form-group
              class="mt-3"
              :label="`One-time code validity (minutes): ${local.two_factor_otp_validity_minutes}`"
              label-for="tfa-otp-mins"
              description="Applies to SMS code lifetime, TOTP acceptance window, and time to complete the second step after password (1–120)."
            >
              <b-form-input
                id="tfa-otp-mins"
                v-model.number="local.two_factor_otp_validity_minutes"
                type="range"
                min="1"
                max="120"
                :disabled="!canEdit"
              />
              <div class="d-flex justify-content-between small text-muted">
                <span>1</span>
                <span>120</span>
              </div>
            </b-form-group>

            <b-alert
              v-if="local.two_factor_delivery_method === 'sms' && !sendSmsEnabled"
              show
              variant="warning"
              class="mt-3"
            >
              <strong>SMS is off for this institution.</strong>
              SMS codes cannot be delivered until
              <strong>Send SMS</strong> is enabled on the <strong>Institution details</strong> tab and saved.
            </b-alert>
          </template>

          <b-alert v-if="formError" show variant="danger" class="mt-3" dismissible @dismissed="formError = ''">
            {{ formError }}
          </b-alert>
          <b-alert v-if="fieldErrors.length" show variant="danger" class="mt-2" dismissible @dismissed="fieldErrors = []">
            <ul class="mb-0 ps-3">
              <li v-for="(e, i) in fieldErrors" :key="i">{{ e }}</li>
            </ul>
          </b-alert>

          <div class="text-center py-3" v-if="canEdit">
            <b-button type="submit" class="akkurate-green-btn" :disabled="saving">
              <b-spinner v-if="saving" small class="me-1" />
              {{ saving ? "Saving…" : "Save security settings" }}
            </b-button>
          </div>
          <p v-else class="small text-muted mb-0 text-center">You can view these settings; only users with update permission can change them.</p>
        </b-form>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { hasPermission } from "@/helper";

function collectErrors(data) {
  if (!data || !data.errors) return [];
  const out = [];
  const err = data.errors;
  if (typeof err === "object" && !Array.isArray(err)) {
    Object.keys(err).forEach((k) => {
      const v = err[k];
      if (Array.isArray(v)) v.forEach((m) => out.push(`${k}: ${m}`));
      else if (v) out.push(`${k}: ${v}`);
    });
  }
  return out;
}

export default {
  name: "InstitutionSecurityTwoFactor",
  props: {
    /** Current institution `send_sms` flag (main form) */
    sendSmsEnabled: {
      type: Boolean,
      default: false,
    },
    /** Synced from GET /settings/institution `data` */
    value: {
      type: Object,
      default: null,
    },
    /** When true, no outer card (e.g. used inside Institution page tabs). */
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      local: {
        two_factor_authentication_enabled: false,
        two_factor_delivery_method: "authenticator",
        two_factor_otp_validity_minutes: 10,
      },
      saving: false,
      formError: "",
      fieldErrors: [],
      deliveryOptions: [
        { text: "Authenticator app (TOTP)", value: "authenticator" },
        { text: "SMS to the user’s profile mobile (phone_number)", value: "sms" },
      ],
    };
  },
  computed: {
    canView() {
      return hasPermission("settings_view");
    },
    canEdit() {
      return hasPermission("settings_update");
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(v) {
        this.syncFromParent(v);
      },
    },
  },
  methods: {
    syncFromParent(v) {
      const d = v || {};
      this.local = {
        two_factor_authentication_enabled: !!d.two_factor_authentication_enabled,
        two_factor_delivery_method: d.two_factor_delivery_method === "sms" ? "sms" : "authenticator",
        two_factor_otp_validity_minutes: this.clampMins(d.two_factor_otp_validity_minutes),
      };
    },
    clampMins(n) {
      const x = parseInt(n, 10);
      if (Number.isNaN(x)) return 10;
      return Math.min(120, Math.max(1, x));
    },
    buildPayload() {
      if (!this.local.two_factor_authentication_enabled) {
        return { two_factor_authentication_enabled: false };
      }
      return {
        two_factor_authentication_enabled: true,
        two_factor_delivery_method: this.local.two_factor_delivery_method,
        two_factor_otp_validity_minutes: this.clampMins(this.local.two_factor_otp_validity_minutes),
      };
    },
    async save() {
      if (!this.canEdit) return;
      this.saving = true;
      this.formError = "";
      this.fieldErrors = [];
      this.$Progress && this.$Progress.start();
      try {
        const res = await ApiService.put("/settings/institution/two-factor", this.buildPayload());
        if (window.toast?.fire) {
          window.toast.fire({ icon: "success", title: res.data?.message || "Security settings saved" });
        }
        this.$emit("saved");
      } catch (e) {
        const st = e?.response?.status;
        const data = e?.response?.data;
        this.formError = data?.message || (st === 422 ? "Please check the form and try again." : "Could not save security settings.");
        this.fieldErrors = collectErrors(data);
      } finally {
        this.saving = false;
        this.$Progress && this.$Progress.finish();
      }
    },
  },
};
</script>
